import React, {useCallback, useContext, useEffect, useState} from "react";
import { Box, Button, Paper, useTheme } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import CustomLoader from "../../../util/component/CustomLoader";
import SectionHeader from "../../../util/component/SectionHeader";
import ContentBody from "../common/content-body/ContentBody";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { IClaimInsertionForm } from "../models/models";
import { CLAIM_INSERTION_FORM_VALIDATION_SCHEMA, CLAIM_INSERTION_INITIAL_VALUE_FORM } from "./config";
import { snackbarTypes } from "../../../util/state/globalVariables";
import { fetchRaData, saveClaim } from "./services/http.services";
import ClaimInsertionRaInfo from "./claim-insertion-ra-info/ClaimInsertionRaInfo";
import ClaimInsertionInfo from "./claim-insertion-info/ClaimInsertionInfo";
import ClaimAttachments from "./claim-attachments/ClaimAttachments";
import dayjs from "dayjs";
import {AxiosError} from "axios";

const ClaimInsertion = () => {
    const theme = useTheme();
    const {t} = useTranslation(['network', 'translation']);
    const navigate = useNavigate();

    const { dispatchGlobalData } = useContext(GlobalContext);

    const [raData, setRaData] = useState<any>(null);

    const params = useParams(); //params.id

    const claimInsertionForm = useForm<IClaimInsertionForm>({
        defaultValues: CLAIM_INSERTION_INITIAL_VALUE_FORM,
        resolver: yupResolver(
            CLAIM_INSERTION_FORM_VALIDATION_SCHEMA
        )
    });

    const setFieldValues = (data: any) => {
        if (!!data) {
            // RA Info
            claimInsertionForm.setValue('claimInsertionRaInfo.raNumber',  data.raNumber);
            claimInsertionForm.setValue('claimInsertionRaInfo.dmsIdentificationCode',  data.bookingDetail.dmsIdentificationCode);
            claimInsertionForm.setValue('claimInsertionRaInfo.pickUpDate',  !!data.bookingDetail.pickupDate ? dayjs(data.bookingDetail.pickupDate) : null);
            claimInsertionForm.setValue('claimInsertionRaInfo.renewalsAndSubstitutions',  data.renewalsSubstitution);
            claimInsertionForm.setValue('claimInsertionRaInfo.invoiceNumber',  data.financialInfo.invoiceNumber);
            claimInsertionForm.setValue('claimInsertionRaInfo.invoiceDMSIdentificationCode',  data.financialInfo.invoiceDmsIdentificationCode);
            claimInsertionForm.setValue('claimInsertionRaInfo.segment',  data.financialInfo.segment);
            claimInsertionForm.setValue('claimInsertionRaInfo.product',  data.financialInfo.product);
            claimInsertionForm.setValue('claimInsertionRaInfo.category',  data.financialInfo.category);
            claimInsertionForm.setValue('claimInsertionRaInfo.customerCode',  data.financialInfo.customerCode);
        }
    }

    const {isFetching, refetch} = useQuery(['claimInsertion', {id: params.id}], () => fetchRaData({id: params.id}), {
        enabled: false,
        onSuccess: (response) => {
            setFieldValues(response);
            setRaData(response);
        },
        onError: (error) => {
            dispatchGlobalData({
                type: 'SET_SNACKBAR',
                payload: {
                    snackbar: {
                        message: t('translation:error.tryLater'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        }
    })

    const saveClaimMutation = useMutation(
        saveClaim,
        {
            onSuccess: (response) => {
                navigate('/network/ra-search');
            },
            onError: (err: AxiosError) => {
                let mess = 'translation:error.tryLater';
                // @ts-ignore
                if (err.response?.status === 400 && err.response?.data?.errorDetails === "\"ERROR_INCONSISTENT_RA_STATE\"") {
                    mess = "common.error.errorInconsistentRaState"
                }
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t(mess),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    );

    const handleSaveClaim = useCallback((data: IClaimInsertionForm) => {
        const payload: any = {
            "claimInfo": {
                "raNumber": data.claimInsertionRaInfo.raNumber,
                "priority": data.claimInsertionInfo.priority,
                "type": data.claimInsertionInfo.type,
                "claimNotes": data.claimInsertionInfo.claimNotes,
                "byLegalRepresentative": data.claimInsertionInfo.byLegalRepresentative,
                "email": data.claimInsertionInfo.email,
                "prefix": data.claimInsertionInfo.prefix,
                "phone": data.claimInsertionInfo.phone,
                "requesterType": data.claimInsertionInfo.requesterType,
                "requesterNotes": data.claimInsertionInfo.requesterNotes,
                "claimAttachments": []
            }
        };
        console.log('payload T0 ', JSON.stringify(payload));
        if (params?.id && params?.id !== '') {
            payload.idRentalAgreement = decodeURIComponent(params?.id);
        }
        console.log('payload T1 ', JSON.stringify(payload));

        data.attachments?.files
            .map((attach: any) =>
                payload.claimInfo.claimAttachments
                    .push({
                        documentTitle: attach.name,
                        documentFile: attach.file
                    })
            );

        saveClaimMutation.mutate(payload);
    },[]);

    const handleCancelClaim = () => {
        claimInsertionForm.reset();
        setFieldValues(raData);
    };

    const handleBack = () => {
        if (params.id) {
            navigate(`/network/ra-detail/${params.id}`);
        } else {
            navigate('/network/ra-search');
        }
    }

    useEffect(() => {
        refetch();
    }, [])

    return (
        <>
            <SectionHeader title={t('claimInsertion.header.title')} />

            <ContentBody>
                <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                    <FormProvider {...claimInsertionForm}>

                        <ClaimInsertionRaInfo />

                        <ClaimInsertionInfo />

                        <ClaimAttachments />

                        <Box sx={{display: "flex", justifyContent: "space-between", mt: theme.spacing(2)}}>
                            <Button variant={"outlined"} color={'secondary'} onClick={handleBack}>
                                {t('claimInsertion.button.back')}
                            </Button>

                            <Box>
                                <Button variant={"outlined"} color={"accent"}
                                        sx={{mx: theme.spacing(1)}}
                                        onClick={handleCancelClaim}>
                                    {t('claimInsertion.button.cancel')}
                                </Button>

                                <Button color={"accent"}
                                        sx={{ml: theme.spacing(1)}}
                                        onClick={claimInsertionForm.handleSubmit(handleSaveClaim)}>
                                    {t('claimInsertion.button.continue')}
                                </Button>
                            </Box>
                        </Box>
                    </FormProvider>
                </Paper>
            </ContentBody>

            <CustomLoader loading={isFetching || saveClaimMutation.isLoading}/>
        </>
    );
}

export default ClaimInsertion;